import React from 'react'
import { Label, Input, FormFeedback, InputGroup } from 'reactstrap'
import {
  Controller,
  FieldValues,
  Control,
  FieldErrors,
  useWatch
} from 'react-hook-form'
import ICSButton from '../../components/ICS/Button/ICSButton'
import ICSToolTip from '../ICSToolTip/ICSToolTip'
import { UseFormTrigger, UseFormSetValue } from 'react-hook-form'

type FileInputFieldValues = {
  file: string
  fileUpload: string
  description: string
  filename: string
  name: string
}

type Trigger = UseFormTrigger<FileInputFieldValues>

const ICSFileInput = ({
  name,
  id = `${name}FileInput`,
  label,
  control,
  defaultValue = 'No File Chosen',
  errors,
  helpText,
  validateFile,
  setValue,
  hidden = true,
  buttonClick,
  accept = '',
  trigger
}: ICSFileInputProps) => {
  const filename = useWatch({
    control,
    name: 'filename',
    defaultValue
  })
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onBlur, name } }) => {
        const IsolateReRender = () => <div>{filename}</div>
        return (
          <>
            <Label for={id}>
              <div className="tooltip-wrapper">
                <ICSButton id={`${id}Button`} onClick={buttonClick}>
                  {label}
                </ICSButton>
                {helpText && (
                  <ICSToolTip
                    id={`${name}ToolTip`}
                    text={helpText}
                    target={`#${name}ToolTip`}
                  />
                )}
              </div>
              <IsolateReRender />
            </Label>
            <InputGroup>
              <Input
                id={id}
                data-testid='file-input'
                onBlur={onBlur}
                onChange={async (e) => {
                  const val = e.target?.files?.[0]
                  setValue('filename', val?.name)
                  trigger('filename')
                  const validated = validateFile && (await validateFile(val))

                  if (validated?.length > 0) {
                    setValue('file', validated)
                    trigger('file')
                  } else {
                    return false
                  }
                }}
                type="file"
                valid={filename !== 'No File Chosen'}
                hidden={hidden}
                invalid={filename === 'No File Chosen'}
                style={{ color: 'black' }}
                accept={accept}
              />

              <FormFeedback
                valid={errors?.[name] ? false : true}
                role="alert"
                id={`${name}Error`}
              >
                {errors?.[name]?.message}
              </FormFeedback>
            </InputGroup>
          </>
        )
      }}
    />
  )
}

export default ICSFileInput

// TODO: Revisit any types
type ICSFileInputProps<TFieldValues extends FieldValues = FieldValues> = {
  id?: string
  name: string
  control: Control<TFieldValues>
  errors: FieldErrors<TFieldValues>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any
  label?: string
  helpText?: string
  isSubmitted?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validateFile?: any
  setValue: UseFormSetValue<FieldValues>
  buttonClick?: () => void | undefined
  hidden?: boolean
  accept?: string
  trigger: Trigger
}
